import type {
  AuthResult,
  Challenge,
  MfaMethod,
  VerificationPurpose,
  VerifiedResult,
} from './types';

const MFA_METHODS = ['sms', 'email'] as const satisfies readonly MfaMethod[];

const isMfaMethod = (value: unknown): value is MfaMethod =>
  MFA_METHODS.includes(value as MfaMethod);

export class AuthRequestError extends Error {
  status: number;
  code?: string;
  resendAvailableAt?: string;

  constructor(
    message: string,
    options: { status?: number; code?: string; resendAvailableAt?: string } = {}
  ) {
    super(message);
    this.name = 'AuthRequestError';
    this.status = options.status ?? 0;
    this.code = options.code;
    this.resendAvailableAt = options.resendAvailableAt;
  }
}

type Body = Record<string, unknown>;

const asBody = (value: unknown): Body =>
  value && typeof value === 'object' ? (value as Body) : {};

const optionalString = (value: unknown): string | undefined =>
  typeof value === 'string' && value ? value : undefined;

const errorMessage = (body: Body): string | undefined => {
  if (Array.isArray(body.message)) return optionalString(body.message[0]);
  if (Array.isArray(body.errors)) {
    return optionalString(asBody(body.errors[0]).message);
  }
  return optionalString(body.message);
};

const assertOk = (status: number, value: unknown, fallback: string): Body => {
  const body = asBody(value);
  if (status < 200 || status >= 300) {
    throw new AuthRequestError(errorMessage(body) ?? fallback, {
      status,
      code: optionalString(body.code),
      resendAvailableAt: optionalString(body.resendAvailableAt),
    });
  }
  return body;
};

const invalidResponse = (status: number) =>
  new AuthRequestError('Unexpected response from the server', { status });

export const parseChallenge = (status: number, value: unknown): Challenge => {
  const body = assertOk(status, value, 'Unable to send a verification code');
  const methods = Array.isArray(body.methods)
    ? body.methods.filter(isMfaMethod)
    : [];
  if (
    typeof body.token !== 'string' ||
    !isMfaMethod(body.method) ||
    !methods.includes(body.method) ||
    typeof body.hint !== 'string' ||
    typeof body.resendAvailableAt !== 'string'
  ) {
    throw invalidResponse(status);
  }
  return {
    token: body.token,
    method: body.method,
    hint: body.hint,
    methods,
    resendAvailableAt: body.resendAvailableAt,
  };
};

export const parseAuthResponse = (
  status: number,
  value: unknown
): AuthResult => {
  const body = assertOk(status, value, 'Unable to sign in');
  if (body.mfaRequired === true) {
    return { kind: 'challenge', challenge: parseChallenge(status, body) };
  }
  if (typeof body.token !== 'string' || !body.token) {
    throw invalidResponse(status);
  }
  return {
    kind: 'authenticated',
    token: body.token,
    shouldPromptForPasskeyRegistration:
      body.shouldPromptForPasskeyRegistration === true,
  };
};

export const parseVerified = <P extends VerificationPurpose>(
  purpose: P,
  status: number,
  body: unknown
): VerifiedResult[P] => {
  const verified = (): VerifiedResult[VerificationPurpose] => {
    switch (purpose) {
      case 'login': {
        const result = parseAuthResponse(status, body);
        if (result.kind !== 'authenticated') throw invalidResponse(status);
        return result;
      }
      case 'set-phone': {
        const { phone } = assertOk(status, body, 'Unable to verify the code');
        if (typeof phone !== 'string') throw invalidResponse(status);
        return { phone };
      }
      case 'set-email': {
        const { email } = assertOk(status, body, 'Unable to verify the code');
        if (typeof email !== 'string') throw invalidResponse(status);
        return { email };
      }
      case 'disable-mfa': {
        const { enabled } = assertOk(status, body, 'Unable to verify the code');
        if (enabled !== false) throw invalidResponse(status);
        return { enabled };
      }
    }
  };
  return verified() as VerifiedResult[P];
};
